/**
 * Organization management API schemas
 */

import { z } from 'zod';
import { defineStoredJsonContract } from '../stored-json-contract.types';
import {
    CredentialRotationAttemptState,
    CredentialType,
    IpxCredentialDefinitionKind,
} from '../secret-contracts';
import { OrganizationAllowedWebsiteSource } from '../allowed-website-contracts';
import {
    PaymentReconciliationStatus,
    PaymentTransactionLifecycleEventType,
    PaymentTransactionStatus,
} from '../payment-contracts';
import { Role,
    Status } from '../identity-contracts';
import {
    CardType,
    DeliveryChannel,
    DeliveryProvider,
    PaymentRail,
    PaymentRequestStatus,
    type CustomerDetails,
} from '../payment.types';
import {
    GatewayResponseHeaderSchema,
    PaymentMethodTokenDetailsSchema,
    PaymentTransactionAmountBreakdownSchema,
    PaymentTransactionConfigurationSchema,
    PaymentRequestSourceDocumentKeySchema,
    PillarPaymentRequestSchema,
    PillarPaymentResponseSchema,
    TransactionDetailsSchema,
} from './payment.schemas';
import { CustomerDetailsSchema } from './customer-details.schemas';
import { JsonObjectSchema, MessageResponseSchema, PositiveIntSchema } from './common.schemas';
import { NumericIpgSystemIdSchema, NumericIpwSystemIdSchema } from './third-party-system.schemas';
import {
    BusinessDetailsSchema,
    BinLookupServiceConfigSchema,
    CustomerPortalPaymentRequestsConfigSchema,
    IpsConfigSchema,
    IpgConfigCustomizationMapSchema,
    IpwConfigCustomizationMapSchema,
    MerchantConfigSchema,
    MerchantConfigStorageWriteSchema,
    RoleAssignmentCustomConfigSchema,
    PlatformPaymentMethodsSchema,
    StoredRpaBuilderWebsiteConfigSchema,
    StoredUnmanagedAllowedWebsiteSchema,
    UpdateIpgConfigCustomizationMapSchema,
    UpdateIpwConfigCustomizationMapSchema,
} from '../backend_config.types';
import { StoredMerchantAccountConfigurationSchema } from '../payments_infrastructure.types';
import {
    StoredLogicalTerminalConfigurationSchema,
    StoredTerminalDeviceConfigurationSchema,
} from './payments-infrastructure.schemas';
import { PermissionGrantAuditFindingCodeSchema } from '../permission.types';
import { MerchantTimeZoneSchema } from '../timezone.utils';
import { normalizeStoredJsonSafeIntegerObjectFields } from '../stored-json-number';
import {
    CredentialOwnerScopeSchema,
    CredentialRotationGenerationMetadataSchema,
    CredentialRotationPersistenceEvidenceSchema,
    CredentialRotationTargetFieldKeysSchema,
    IpxCredentialSummaryCapabilitiesSchema,
} from './secret.schemas';
import { GatewayLifecycleRequestSchema } from './ipg/gateway-lifecycle-request.schemas';
import { GatewayLifecycleResponseJsonBodySchema } from './ipg/gateway-lifecycle-response.schemas';
import type {
    ApplyOrganizationUsersCsvImportRequest,
    ApplyOrganizationUsersCsvImportResult,
    ApplyOrganizationUsersCsvImportResponse,
    CreateOrganizationDocumentTemplateRequest,
    DeleteOrganizationResponse,
    CreateIsoResponse,
    CreateMerchantResponse,
    DocumentTemplateKind,
    DocumentTemplateOutputMime,
    DocumentTemplatePayloadType,
    DocumentTemplateSelectorJson,
    DocumentTemplateSelector,
    DocumentTemplateValidationIssue,
    DocumentTemplateValidationIssueCode,
    GetOrganizationResponse,
    GetMerchantChecklistCheckResponse,
    GetMerchantChecklistResponse,
    ListOrganizationDocumentTemplatesRequest,
    ListOrganizationDocumentTemplatesResponse,
    MerchantBackupCollectionName,
    MerchantBackupCredentialRecord,
    MerchantBackupAllowedWebsiteTemplateRecord,
    MerchantBackupAllowedWebsiteTemplateRecordV2,
    MerchantBackupAchStoredPaymentDetailsRecord,
    MerchantBackupCreditCardStoredPaymentDetailsRecord,
    MerchantBackupDebitCardStoredPaymentDetailsRecord,
    MerchantBackupDocumentTemplateRecord,
    MerchantBackupIpwCustomerLinkRecord,
    MerchantBackupIpxCredentialDefinitionRecord,
    MerchantBackupIpxCredentialDefinitionRecordV2,
    MerchantBackupIpxRbsWorkflowInstanceRecord,
    MerchantBackupIpxWebResourceModelRecord,
    MerchantBackupLegalDocumentRecord,
    MerchantBackupMerchantCustomerRecord,
    MerchantBackupMode,
    MerchantBackupOrganizationAllowedWebsiteRecord,
    MerchantBackupOrganizationAllowedWebsiteRecordV2,
    MerchantBackupOrganizationRpaBuilderWebsiteRecord,
    MerchantBackupOrganizationIpxCredentialPolicyRecord,
    MerchantBackupPasswordRecord,
    MerchantBackupPaymentRequestAccessTokenRecord,
    MerchantBackupPaymentMethodTokenRecord,
    MerchantBackupPaymentRequestDeliveryEventRecord,
    MerchantBackupPaymentRequestDocumentRecord,
    MerchantBackupPaymentRequestRecord,
    MerchantBackupPaymentTransactionLifecycleEventRecord,
    MerchantBackupPaymentTransactionRecord,
    MerchantBackupProcessorRecord,
    MerchantBackupRestoreCollectionSummary,
    MerchantBackupRestoreCollectionSummaryMap,
    MerchantBackupRestoreSummary,
    MerchantBackupStoredPaymentMethodRecord,
    MerchantBackupCredentialRotationAttemptRecord,
    MerchantBackupTotpBackupCodeRecord,
    MerchantBackupTotpSecretRecord,
    MerchantBackupUserLegalDocumentAcceptanceRecord,
    MerchantBackup,
    MerchantBackupV1,
    MerchantBackupV2,
    MerchantChecklistCredentialIssue,
    MerchantChecklistIssue,
    MerchantChecklistIssueCapabilities,
    MerchantChecklistTerminalAccessIssue,
    MerchantBackupLogicalTerminalRecord,
    MerchantBackupMerchantAccountRecord,
    MerchantChecklistTerminalIssue,
    MerchantChecklistTerminalIssueCapabilities,
    MerchantChecklistUserIssue,
    MerchantBackupOrganizationRecord,
    MerchantBackupPermissionsBundleRecord,
    MerchantBackupPermissionsPolicyGrantRecord,
    MerchantBackupRoleAssignmentRecord,
    MerchantBackupTerminalDeviceRecord,
    MerchantBackupUserRecord,
    ExportMerchantBackupRequest,
    OrganizationUserCsvImportMatchedUserSummary,
    OrganizationUserCsvImportPreviewRow,
    OrganizationUserCsvImportRowClassification,
    OrganizationUserCsvImportRole,
    OrganizationUserCsvImportUserData,
    OrganizationListItem,
    OrganizationOmnisearchListItem,
    OrganizationDocumentTemplateOverrideDescriptor,
    OrganizationDocumentTemplateRegistryEntry,
    OrganizationSearchMerchantAccountSummaryItem,
    PreviewOrganizationUsersCsvImportRequest,
    PreviewOrganizationUsersCsvImportResponse,
    RestoreMerchantBackupApplyRequest,
    RestoreMerchantBackupApplyResponse,
    RestoreMerchantBackupDryRunRequest,
    RestoreMerchantBackupDryRunResponse,
    SetOrganizationDocumentTemplateActiveRequest,
    SetOrganizationParentResponse,
    UpdateOrganizationRequest,
    UpdateOrganizationDocumentTemplateRequest,
    UpdateOrganizationResponse,
    ValidateOrganizationDocumentTemplateFailureResponse,
    ValidateOrganizationDocumentTemplateRequest,
    ValidateOrganizationDocumentTemplateResponse,
    ValidateOrganizationDocumentTemplateSuccessResponse,
} from '../organization.types';
import { StoredRbsWebsiteStorageV1Schema } from './rbs-website.schemas';

const MerchantBackupCustomerDetailsSchema: z.ZodType<CustomerDetails> = z.lazy(
    () => CustomerDetailsSchema
);

export const PaymentRequestEmailCcSchema: z.ZodType<string[]> = z.array(z.string().email());

/**
 * Organization list item
 */
export const OrganizationSearchMerchantAccountSummaryItemSchema: z.ZodType<OrganizationSearchMerchantAccountSummaryItem> =
    z.object({
        processor_mid: z.string(),
        surcharge_enabled: z.boolean(),
        surcharge_pct: z.number(),
    });

export const OrganizationListItemSchema: z.ZodType<OrganizationListItem> = z.object({
    organization_id: z.number(),
    organization_uuid: z.string().uuid().optional(),
    name: z.string(),
    omnisearch_text: z.string().optional(),
    processor_mids: z.array(z.string()).optional(),
    merchant_accounts: z.array(OrganizationSearchMerchantAccountSummaryItemSchema).optional(),
    is_iso: z.boolean(),
    is_merchant: z.boolean(),
    is_customer: z.boolean(),
    parent_organization_id: z.number().nullable(),
    parent_organization_uuid: z.string().uuid().nullable().optional(),
    iso_organization_uuid: z.string().uuid().nullable().optional(),
    status: z.enum(Status),
    businessDetails: BusinessDetailsSchema.optional(),
    detected_time_zone: MerchantTimeZoneSchema.optional(),
    ips: IpsConfigSchema.optional(),
    ipwConfigCustomizations: IpwConfigCustomizationMapSchema.optional(),
    ipgConfigCustomizations: IpgConfigCustomizationMapSchema.optional(),
    merchantPaymentMethods: PlatformPaymentMethodsSchema.optional(),
    binLookupService: BinLookupServiceConfigSchema.nullable().optional(),
    require2FA: z.boolean().optional(),
    customerPortalPaymentRequests: CustomerPortalPaymentRequestsConfigSchema.optional(),
    merchantPaymentConfirmationEmailCc: PaymentRequestEmailCcSchema.optional(),
    enable_ipg_system_ids: z.array(NumericIpgSystemIdSchema).optional(),
    enable_ipw_system_ids: z.array(NumericIpwSystemIdSchema).optional(),
    merchant_count: z.number().optional(),
});

export const OrganizationOmnisearchListItemSchema: z.ZodType<OrganizationOmnisearchListItem> =
    z.object({
        organization_uuid: z.string().uuid(),
        name: z.string(),
        omnisearch_text: z.string().optional(),
        processor_mids: z.array(z.string()).optional(),
        merchant_accounts: z.array(OrganizationSearchMerchantAccountSummaryItemSchema).optional(),
        is_iso: z.boolean(),
        is_merchant: z.boolean(),
        is_customer: z.boolean(),
        parent_organization_uuid: z.string().uuid().nullable(),
        iso_organization_uuid: z.string().uuid().nullable().optional(),
        status: z.enum(Status),
        businessDetails: BusinessDetailsSchema.optional(),
        detected_time_zone: MerchantTimeZoneSchema.optional(),
        ips: IpsConfigSchema.optional(),
        ipwConfigCustomizations: IpwConfigCustomizationMapSchema.optional(),
        ipgConfigCustomizations: IpgConfigCustomizationMapSchema.optional(),
        merchantPaymentMethods: PlatformPaymentMethodsSchema.optional(),
        binLookupService: BinLookupServiceConfigSchema.nullable().optional(),
        require2FA: z.boolean().optional(),
        customerPortalPaymentRequests: CustomerPortalPaymentRequestsConfigSchema.optional(),
        merchantPaymentConfirmationEmailCc: PaymentRequestEmailCcSchema.optional(),
        enable_ipg_system_ids: z.array(NumericIpgSystemIdSchema).optional(),
        enable_ipw_system_ids: z.array(NumericIpwSystemIdSchema).optional(),
        merchant_count: z.number().optional(),
    });

/**
 * Create ISO request
 */
export const CreateIsoRequestSchema = z.object({
    name: z.string().min(1),
    parent_organization_uuid: z.string().uuid().optional(),
});

/**
 * Create merchant request
 */
export const CreateMerchantRequestSchema = z.object({
    name: z.string().min(1),
    parent_organization_uuid: z.string().uuid().optional(),
    iso_organization_uuid: z.string().uuid().optional(),
});

/**
 * Assign merchant to ISO request
 */
export const AssignMerchantToIsoRequestSchema = z.object({
    merchant_organization_uuid: z.string().uuid(),
    iso_organization_uuid: z.string().uuid().nullable(),
});

/**
 * Update organization request
 */
export const UpdateOrganizationRequestSchema: z.ZodType<UpdateOrganizationRequest> = z
    .object({
        name: z.string().min(1).optional(),
        status: z.enum(Status).optional(),
        iso_organization_uuid: z.string().uuid().nullable().optional(),
        businessDetails: BusinessDetailsSchema.nullable().optional(),
        ips: IpsConfigSchema.partial().optional(),
        ipwConfigCustomizations: UpdateIpwConfigCustomizationMapSchema.optional(),
        ipgConfigCustomizations: UpdateIpgConfigCustomizationMapSchema.optional(),
        merchantPaymentMethods: PlatformPaymentMethodsSchema.nullable().optional(),
        binLookupService: BinLookupServiceConfigSchema.nullable().optional(),
        require2FA: z.boolean().optional(),
        customerPortalPaymentRequests:
            CustomerPortalPaymentRequestsConfigSchema.nullable().optional(),
        merchantPaymentConfirmationEmailCc: PaymentRequestEmailCcSchema.nullable().optional(),
        enable_ipg_system_ids: z.array(NumericIpgSystemIdSchema).optional(),
        enable_ipw_system_ids: z.array(NumericIpwSystemIdSchema).optional(),
    })
    .strict();

/**
 * Assign merchant to ISO response
 */
export const AssignMerchantToIsoResponseSchema = z.object({
    organization_uuid: z.string().uuid(),
    name: z.string(),
    parent_organization_uuid: z.string().uuid().nullable(),
    iso_organization_uuid: z.string().uuid().nullable().optional(),
});

/**
 * Set organization parent request
 */
export const SetOrganizationParentRequestSchema = z.object({
    organization_uuid: z.string().uuid(),
    parent_organization_uuid: z.string().uuid().nullable(),
});

/**
 * Set organization parent response
 */
export const SetOrganizationParentResponseSchema: z.ZodType<SetOrganizationParentResponse> = z.lazy(
    () => UpdateOrganizationResponseSchema
);

/**
 * Delete organization response
 */
export const DeleteOrganizationResponseSchema: z.ZodType<DeleteOrganizationResponse> =
    MessageResponseSchema;

/**
 * Create ISO response
 */
export const CreateIsoResponseSchema: z.ZodType<CreateIsoResponse> = z.object({
    organization_uuid: z.string().uuid(),
    name: z.string(),
    is_iso: z.boolean(),
    is_merchant: z.boolean(),
    is_customer: z.boolean(),
    parent_organization_uuid: z.string().uuid().nullable(),
    iso_organization_uuid: z.string().uuid().nullable().optional(),
    status: z.enum(Status),
    enable_ipg_system_ids: z.array(NumericIpgSystemIdSchema),
    enable_ipw_system_ids: z.array(NumericIpwSystemIdSchema),
});

/**
 * Create merchant response
 */
export const CreateMerchantResponseSchema: z.ZodType<CreateMerchantResponse> = z.object({
    organization_uuid: z.string().uuid(),
    name: z.string(),
    is_iso: z.boolean(),
    is_merchant: z.boolean(),
    is_customer: z.boolean(),
    parent_organization_uuid: z.string().uuid().nullable(),
    iso_organization_uuid: z.string().uuid().nullable().optional(),
    status: z.enum(Status),
    enable_ipg_system_ids: z.array(NumericIpgSystemIdSchema),
    enable_ipw_system_ids: z.array(NumericIpwSystemIdSchema),
});

/**
 * Update organization response
 */
export const UpdateOrganizationResponseSchema: z.ZodType<UpdateOrganizationResponse> = z.object({
    organization_uuid: z.string().uuid(),
    name: z.string(),
    omnisearch_text: z.string().optional(),
    processor_mids: z.array(z.string()).optional(),
    merchant_accounts: z.array(OrganizationSearchMerchantAccountSummaryItemSchema).optional(),
    is_iso: z.boolean(),
    is_merchant: z.boolean(),
    is_customer: z.boolean(),
    parent_organization_uuid: z.string().uuid().nullable(),
    iso_organization_uuid: z.string().uuid().nullable().optional(),
    status: z.enum(Status),
    businessDetails: BusinessDetailsSchema.optional(),
    detected_time_zone: MerchantTimeZoneSchema.optional(),
    ips: IpsConfigSchema.optional(),
    ipwConfigCustomizations: IpwConfigCustomizationMapSchema.optional(),
    ipgConfigCustomizations: IpgConfigCustomizationMapSchema.optional(),
    merchantPaymentMethods: PlatformPaymentMethodsSchema.optional(),
    binLookupService: BinLookupServiceConfigSchema.nullable().optional(),
    require2FA: z.boolean().optional(),
    customerPortalPaymentRequests: CustomerPortalPaymentRequestsConfigSchema.optional(),
    merchantPaymentConfirmationEmailCc: PaymentRequestEmailCcSchema.optional(),
    enable_ipg_system_ids: z.array(NumericIpgSystemIdSchema).optional(),
    enable_ipw_system_ids: z.array(NumericIpwSystemIdSchema).optional(),
    merchant_count: z.number().optional(),
});

/**
 * Get organization response
 */
export const GetOrganizationResponseSchema: z.ZodType<GetOrganizationResponse> =
    UpdateOrganizationResponseSchema;

/**
 * Common param/query schemas
 */
export const OrganizationIdParamSchema: z.ZodType<number> = PositiveIntSchema;
export const OrganizationUuidParamSchema: z.ZodType<string> = z.string().uuid();
export const IsoIdQuerySchema: z.ZodType<number> = PositiveIntSchema;

const JsonRecordSchema = z.record(z.string(), z.unknown());

export const OrganizationUserCsvImportRowClassificationSchema: z.ZodType<OrganizationUserCsvImportRowClassification> =
    z.enum(['insert', 'noop', 'update', 'csv_duplicate_error', 'db_ambiguous_match_error']);

export const OrganizationUserCsvImportRoleSchema: z.ZodType<OrganizationUserCsvImportRole> = z.enum(
    ['PRINCIPAL', 'ADMIN', 'REP']
);

export const OrganizationUserCsvImportUserDataSchema: z.ZodType<OrganizationUserCsvImportUserData> =
    z.object({
        first_name: z.string().min(1).optional(),
        last_name: z.string().min(1).optional(),
        emails: z.array(z.string().email()),
        phones: z.array(z.string().min(1)),
        primary_email: z.string().email(),
        primary_phone: z.string().min(1).optional(),
        role: OrganizationUserCsvImportRoleSchema,
        policies: z.array(z.string().min(1)),
    });

export const OrganizationUserCsvImportMatchedUserSummarySchema: z.ZodType<OrganizationUserCsvImportMatchedUserSummary> =
    z.object({
        user_uuid: z.string().uuid(),
        first_name: z.string().min(1).optional(),
        last_name: z.string().min(1).optional(),
        emails: z.array(z.string().email()),
        phones: z.array(z.string().min(1)),
        primary_email: z.string().email(),
        primary_phone: z.string().min(1).optional(),
        role: OrganizationUserCsvImportRoleSchema,
        policies: z.array(z.string().min(1)),
    });

export const PreviewOrganizationUsersCsvImportRequestSchema: z.ZodType<PreviewOrganizationUsersCsvImportRequest> =
    z.object({
        csv_text: z.string().min(1),
    });

export const OrganizationUserCsvImportPreviewRowSchema: z.ZodType<OrganizationUserCsvImportPreviewRow> =
    z.object({
        row_index: z.number().int().positive(),
        classification: OrganizationUserCsvImportRowClassificationSchema,
        row_data: OrganizationUserCsvImportUserDataSchema,
        row_errors: z.array(z.string().min(1)).optional(),
        existing_user: OrganizationUserCsvImportMatchedUserSummarySchema.optional(),
        conflicting_users: z.array(OrganizationUserCsvImportMatchedUserSummarySchema).optional(),
    });

export const PreviewOrganizationUsersCsvImportResponseSchema: z.ZodType<PreviewOrganizationUsersCsvImportResponse> =
    z.object({
        rows: z.array(OrganizationUserCsvImportPreviewRowSchema),
    });

export const ApplyOrganizationUsersCsvImportRequestSchema: z.ZodType<ApplyOrganizationUsersCsvImportRequest> =
    z.object({
        csv_text: z.string().min(1),
        selected_row_indexes: z.array(z.number().int().positive()).min(1),
        skip_verification: z.boolean().optional(),
    });

export const ApplyOrganizationUsersCsvImportResultClassificationSchema: z.ZodType<
    ApplyOrganizationUsersCsvImportResult['classification']
> = z.enum(['insert', 'update']);

export const ApplyOrganizationUsersCsvImportResultSchema: z.ZodType<ApplyOrganizationUsersCsvImportResult> =
    z.object({
        row_index: z.number().int().positive(),
        classification: ApplyOrganizationUsersCsvImportResultClassificationSchema,
        user_uuid: z.string().uuid().nullable(),
    });

export const ApplyOrganizationUsersCsvImportResponseSchema: z.ZodType<ApplyOrganizationUsersCsvImportResponse> =
    z.object({
        results: z.array(ApplyOrganizationUsersCsvImportResultSchema),
    });

export const DocumentTemplateKindSchema: z.ZodType<DocumentTemplateKind> = z.enum([
    'PAYMENT_RECEIPT',
    'INVOICE',
    'PAYMENT_REQUEST_EMAIL',
    'PAYMENT_REQUEST_SMS',
]);

export const DocumentTemplateOutputMimeSchema: z.ZodType<DocumentTemplateOutputMime> = z.enum([
    'TEXT',
    'HTML',
]);

export const DocumentTemplatePayloadTypeSchema: z.ZodType<DocumentTemplatePayloadType> = z.enum([
    'TRANSACTION_DETAILS',
    'PAYMENT_REQUEST',
    'PAYMENT_TRANSACTION',
]);

export const DocumentTemplateSelectorJsonVersionSchema: z.ZodType<
    DocumentTemplateSelectorJson['document_template_selector_json_version']
> = z.literal(1);

export const DocumentTemplateSelectorJsonSchema: z.ZodType<DocumentTemplateSelectorJson> = z.object(
    {
        document_template_selector_json_version: DocumentTemplateSelectorJsonVersionSchema,
        selector_values: JsonObjectSchema,
    }
);

export const StoredDocumentTemplateSelectorJsonParser: z.ZodType<DocumentTemplateSelectorJson> =
    z.preprocess(
        (value: unknown): unknown =>
            normalizeStoredJsonSafeIntegerObjectFields(value, {
                domain: 'DocumentTemplateSelectorJson',
                fields: ['document_template_selector_json_version'],
                minimum: 1,
                maximum: 1,
            }),
        DocumentTemplateSelectorJsonSchema
    );

export function normalizeDocumentTemplateSelectorStorageV1(
    selector: DocumentTemplateSelectorJson
): DocumentTemplateSelectorJson {
    return selector;
}

export const DocumentTemplateSelectorStorageContract = defineStoredJsonContract({
    contractName: 'DocumentTemplateSelector',
    semanticFieldValues: {},
    variants: {
        v1: {
            schemaName: 'StoredDocumentTemplateSelectorJsonParser',
            schema: StoredDocumentTemplateSelectorJsonParser,
            guards: [],
            normalize: normalizeDocumentTemplateSelectorStorageV1,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
    },
    writerVariant: 'v1',
});

export function serializeDocumentTemplateSelectorJsonV1(
    value: DocumentTemplateSelectorJson
): DocumentTemplateSelectorJson {
    return DocumentTemplateSelectorJsonSchema.parse(value);
}

export const DocumentTemplateSelectorSchema: z.ZodType<DocumentTemplateSelector> = z.object({
    template_kind: DocumentTemplateKindSchema,
    template_output_mime: DocumentTemplateOutputMimeSchema,
    payload_type: DocumentTemplatePayloadTypeSchema,
    payload_discriminator: z.string().min(1),
    payload_type_version: z.number().int().nonnegative(),
    selector_json: DocumentTemplateSelectorJsonSchema.optional(),
});

export const OrganizationDocumentTemplateRegistryEntrySchema: z.ZodType<OrganizationDocumentTemplateRegistryEntry> =
    z.object({
        selector: DocumentTemplateSelectorSchema,
        name: z.string().min(1),
        description: z.string().min(1).optional(),
        default_template_source: z.string().min(1),
        allowed_root_variables: z.array(z.string().min(1)),
    });

export const OrganizationDocumentTemplateOverrideDescriptorSchema: z.ZodType<OrganizationDocumentTemplateOverrideDescriptor> =
    z.object({
        document_template_uuid: z.string().uuid(),
        selector: DocumentTemplateSelectorSchema,
        name: z.string().min(1),
        description: z.string().min(1).optional(),
        template_source: z.string().min(1),
        content_sha256: z.string().min(1),
        status: z.enum(Status),
    });

export const ListOrganizationDocumentTemplatesResponseSchema: z.ZodType<ListOrganizationDocumentTemplatesResponse> =
    z.object({
        registry_entries: z.array(OrganizationDocumentTemplateRegistryEntrySchema),
        overrides: z.array(OrganizationDocumentTemplateOverrideDescriptorSchema),
    });

export const ListOrganizationDocumentTemplatesRequestSchema: z.ZodType<ListOrganizationDocumentTemplatesRequest> =
    z
        .object({
            show_deleted: z.coerce.boolean().optional(),
        })
        .strict();

export const DocumentTemplateValidationIssueCodeSchema: z.ZodType<DocumentTemplateValidationIssueCode> =
    z.enum([
        'UNSUPPORTED_SELECTOR',
        'TEMPLATE_TOO_LARGE',
        'PARSE_ERROR',
        'FORBIDDEN_TAG',
        'FORBIDDEN_FILTER',
        'FORBIDDEN_VARIABLE_ROOT',
    ]);

export const DocumentTemplateValidationIssueSchema: z.ZodType<DocumentTemplateValidationIssue> =
    z.object({
        code: DocumentTemplateValidationIssueCodeSchema,
        message: z.string().min(1),
    });

export const ValidateOrganizationDocumentTemplateRequestSchema: z.ZodType<ValidateOrganizationDocumentTemplateRequest> =
    z.object({
        selector: DocumentTemplateSelectorSchema,
        template_source: z.string().min(1),
    });

export const ValidateOrganizationDocumentTemplateSuccessResponseSchema: z.ZodType<ValidateOrganizationDocumentTemplateSuccessResponse> =
    z.object({
        valid: z.literal(true),
        referenced_root_variables: z.array(z.string().min(1)),
        used_filter_names: z.array(z.string().min(1)),
        used_tag_names: z.array(z.string().min(1)),
    });

export const ValidateOrganizationDocumentTemplateFailureResponseSchema: z.ZodType<ValidateOrganizationDocumentTemplateFailureResponse> =
    z.object({
        valid: z.literal(false),
        issues: z.array(DocumentTemplateValidationIssueSchema),
    });

export const ValidateOrganizationDocumentTemplateResponseSchema: z.ZodType<ValidateOrganizationDocumentTemplateResponse> =
    z.union([
        ValidateOrganizationDocumentTemplateSuccessResponseSchema,
        ValidateOrganizationDocumentTemplateFailureResponseSchema,
    ]);

export const CreateOrganizationDocumentTemplateRequestSchema: z.ZodType<CreateOrganizationDocumentTemplateRequest> =
    z.object({
        selector: DocumentTemplateSelectorSchema,
        name: z.string().min(1),
        description: z.string().min(1).optional(),
        template_source: z.string().min(1),
        status: z.enum(Status).optional(),
    });

export const UpdateOrganizationDocumentTemplateRequestSchema: z.ZodType<UpdateOrganizationDocumentTemplateRequest> =
    z.object({
        expected_override: OrganizationDocumentTemplateOverrideDescriptorSchema,
        selector: DocumentTemplateSelectorSchema,
        name: z.string().min(1),
        description: z.string().min(1).optional(),
        template_source: z.string().min(1),
        status: z.enum(Status).optional(),
    });

export const SetOrganizationDocumentTemplateActiveRequestSchema: z.ZodType<SetOrganizationDocumentTemplateActiveRequest> =
    z.object({
        status: z.enum(Status),
    });

const MerchantBackupSourcePrimaryKeyFieldNames: readonly string[] = [
    'organization_id',
    'user_id',
    'permissions_bundle_id',
    'role_assignment_id',
    'permissions_policy_grant_id',
    'credential_id',
    'merchant_account_id',
    'logical_terminal_id',
    'terminal_device_id',
    'password_id',
    'totp_secret_id',
    'totp_backup_code_id',
    'document_template_id',
    'legal_document_id',
    'processor_id',
    'payment_method_token_id',
    'stored_payment_method_id',
    'credit_card_stored_payment_details_id',
    'debit_card_stored_payment_details_id',
    'ach_stored_payment_details_id',
    'payment_transaction_id',
    'payment_request_id',
    'payment_request_delivery_event_id',
    'payment_request_document_id',
    'payment_transaction_lifecycle_event_id',
    'ipw_customer_link_id',
    'merchant_customer_record_id',
    'user_legal_document_acceptance_id',
    'ipx_credential_definition_id',
    'ipx_web_resource_model_id',
    'allowed_website_template_id',
    'organization_allowed_website_id',
    'ipx_rbs_workflow_instance_id',
    'organization_ipx_credential_policy_id',
    'credential_rotation_attempt_id',
    'single_use_auth_token_id',
    'table_pkey',
    'audit_id',
];

export const MerchantBackupCollectionNameSchema: z.ZodType<MerchantBackupCollectionName> = z.enum([
    'organizations',
    'users',
    'permissions_bundles',
    'role_assignments',
    'permissions_policy_grants',
    'credentials',
    'merchant_accounts',
    'logical_terminals',
    'terminal_devices',
    'passwords',
    'totp_secrets',
    'totp_backup_codes',
    'document_templates',
    'legal_documents',
    'processors',
    'payment_method_tokens',
    'stored_payment_methods',
    'credit_card_stored_payment_details',
    'debit_card_stored_payment_details',
    'ach_stored_payment_details',
    'payment_transactions',
    'payment_requests',
    'payment_request_delivery_events',
    'payment_request_documents',
    'payment_transaction_lifecycle_events',
    'ipw_customer_links',
    'merchant_customer_records',
    'user_legal_document_acceptances',
    'ipx_credential_definitions',
    'ipx_web_resource_models',
    'allowed_website_templates',
    'organization_allowed_websites',
    'organization_rpa_builder_websites',
    'ipx_rbs_workflow_instances',
    'organization_ipx_credential_policies',
    'credential_rotation_attempts',
    'payment_request_access_tokens',
]);

export const MerchantBackupRestoreCollectionSummarySchema: z.ZodType<MerchantBackupRestoreCollectionSummary> =
    z
        .object({
            create: z.number().int().nonnegative(),
            reuse: z.number().int().nonnegative(),
            prerequisite: z.number().int().nonnegative(),
            excluded: z.number().int().nonnegative(),
            warning: z.number().int().nonnegative(),
            error: z.number().int().nonnegative(),
        })
        .strict();

export const MerchantBackupRestoreCollectionSummaryMapSchema: z.ZodType<MerchantBackupRestoreCollectionSummaryMap> =
    z.partialRecord(
        MerchantBackupCollectionNameSchema,
        MerchantBackupRestoreCollectionSummarySchema
    );

function addMerchantBackupFullModeRequiredFieldIssue(
    ctx: z.RefinementCtx,
    path: (string | number)[],
    field: string
): void {
    ctx.addIssue({
        code: 'custom',
        path: [...path, field],
        message: `${field} is required for full merchant backups`,
    });
}

function addMerchantBackupFullModeRejectedFieldIssue(
    ctx: z.RefinementCtx,
    path: (string | number)[],
    field: string
): void {
    ctx.addIssue({
        code: 'custom',
        path: [...path, field],
        message: `${field} is not allowed in full merchant backups`,
    });
}

function rejectMerchantBackupJsonArraySourcePrimaryKeys(
    ctx: z.RefinementCtx,
    value: readonly unknown[],
    path: (string | number)[]
): void {
    value.forEach((item: unknown, index: number) => {
        rejectMerchantBackupJsonValueSourcePrimaryKeys(ctx, item, [...path, index]);
    });
}

function rejectMerchantBackupJsonObjectSourcePrimaryKeys(
    ctx: z.RefinementCtx,
    value: Record<string, unknown>,
    path: (string | number)[]
): void {
    for (const field of Object.keys(value)) {
        const fieldValue: unknown = value[field];
        if (MerchantBackupSourcePrimaryKeyFieldNames.includes(field)) {
            ctx.addIssue({
                code: 'custom',
                path: [...path, field],
                message: `${field} is a source database primary key`,
            });
        }
        rejectMerchantBackupJsonValueSourcePrimaryKeys(ctx, fieldValue, [...path, field]);
    }
}

function rejectMerchantBackupJsonValueSourcePrimaryKeys(
    ctx: z.RefinementCtx,
    value: unknown,
    path: (string | number)[]
): void {
    if (Array.isArray(value)) {
        rejectMerchantBackupJsonArraySourcePrimaryKeys(ctx, value, path);
        return;
    }

    const objectParseResult = JsonRecordSchema.safeParse(value);
    if (objectParseResult.success) {
        rejectMerchantBackupJsonObjectSourcePrimaryKeys(ctx, objectParseResult.data, path);
    }
}

export const MerchantBackupOrganizationRecordSchema: z.ZodType<MerchantBackupOrganizationRecord> = z
    .object({
        organization_uuid: z.string().uuid(),
        parent_organization_uuid: z.string().uuid().nullable(),
        iso_organization_uuid: z.string().uuid().nullable().optional(),
        name: z.string().min(1),
        is_iso: z.boolean(),
        is_merchant: z.boolean(),
        is_customer: z.boolean(),
        status: z.enum(Status),
        enable_ipg_system_ids: z.array(NumericIpgSystemIdSchema).optional(),
        enable_ipw_system_ids: z.array(NumericIpwSystemIdSchema).optional(),
        org_config: MerchantConfigSchema,
        omnisearch_text: z.string(),
    })
    .strict();

export const MerchantBackupUserRecordSchema: z.ZodType<MerchantBackupUserRecord> = z
    .object({
        user_uuid: z.string().uuid(),
        first_name: z.string().nullable(),
        last_name: z.string().nullable(),
        emails: z.array(z.string().min(1)),
        phones: z.array(z.string().min(1)).optional(),
        primary_email: z.string().min(1),
        primary_phone: z.string().min(1).nullable().optional(),
        password_hash: z.string().min(1),
        totp_secret: z.string().nullable(),
        totp_enabled: z.boolean(),
        status: z.enum(Status),
        mfa_required: z.boolean().optional(),
        mfa_email_allowed: z.boolean().optional(),
        mfa_sms_allowed: z.boolean().optional(),
        custom_config: MerchantConfigStorageWriteSchema.optional(),
        default_role_assignment_uuid: z.string().uuid().nullable(),
        omnisearch_text: z.string(),
    })
    .strict();

export const MerchantBackupPermissionsBundleRecordSchema: z.ZodType<MerchantBackupPermissionsBundleRecord> =
    z
        .object({
            permissions_bundle_uuid: z.string().uuid(),
            organization_uuid: z.string().uuid().nullable(),
            name: z.string().min(1),
            description: z.string().nullable(),
            permissions: z.array(z.unknown()),
            is_personal: z.boolean(),
            status: z.enum(Status).optional(),
            omnisearch_text: z.string(),
        })
        .strict();

export const MerchantBackupRoleAssignmentRecordSchema: z.ZodType<MerchantBackupRoleAssignmentRecord> =
    z
        .object({
            role_assignment_uuid: z.string().uuid(),
            user_uuid: z.string().uuid(),
            organization_uuid: z.string().uuid().nullable(),
            role: z.enum(Role),
            status: z.enum(Status).optional(),
            user_config: RoleAssignmentCustomConfigSchema,
            permissions_bundle_uuid: z.string().uuid(),
        })
        .strict();

export const MerchantBackupPermissionsPolicyGrantRecordSchema: z.ZodType<MerchantBackupPermissionsPolicyGrantRecord> =
    z
        .object({
            permissions_policy_grant_uuid: z.string().uuid(),
            role_assignment_uuid: z.string().uuid(),
            permissions_bundle_uuid: z.string().uuid(),
            status: z.enum(Status).optional(),
        })
        .strict();

export const MerchantBackupCredentialRecordSchema: z.ZodType<MerchantBackupCredentialRecord> = z
    .object({
        credential_uuid: z.string().uuid(),
        credential_type: z.enum(CredentialType),
        organization_uuid: z.string().uuid(),
        role_assignment_uuid: z.string().uuid().nullable().optional(),
        ipx_credential_definition_uuid: z.string().uuid().optional(),
        system_id: z.number().int(),
        credential_definition_key: z.string().optional(),
        account_id: z.string().min(1),
        credential_payload: JsonRecordSchema.optional(),
        encrypted_credentials_base64: z.string().min(1).optional(),
        encryption_algorithm: z.string().min(1).optional(),
        credential_label: z.string().nullable(),
        masked_preview: z.string().nullable(),
        public_fields: JsonRecordSchema,
        applies_to_children: z.boolean(),
        status: z.enum(Status),
        omnisearch_text: z.string(),
    })
    .strict();

export const MerchantBackupMerchantAccountRecordSchema: z.ZodType<MerchantBackupMerchantAccountRecord> =
    z.object({
        merchant_account_uuid: z.string().uuid(),
        processor_name: z.string().min(1),
        processor_mid: z.string().min(1),
        organization_uuid: z.string().uuid(),
        applies_to_children: z.boolean(),
        status: z.enum(Status),
        merchant_account_configuration: StoredMerchantAccountConfigurationSchema,
        omnisearch_text: z.string(),
    });

export const MerchantBackupLogicalTerminalRecordSchema: z.ZodType<MerchantBackupLogicalTerminalRecord> =
    z.object({
        logical_terminal_uuid: z.string().uuid(),
        merchant_account_uuid: z.string().uuid(),
        ipg_system_id: NumericIpgSystemIdSchema,
        processor_tid: z.string().min(1),
        name: z.string().nullable(),
        organization_uuid: z.string().uuid().nullable(),
        applies_to_children: z.boolean().nullable(),
        status: z.enum(Status),
        logical_terminal_configuration: StoredLogicalTerminalConfigurationSchema,
        credential_uuid: z.string().uuid().nullable(),
        credential_credential_type: z.enum(CredentialType).nullable(),
        omnisearch_text: z.string(),
    });

export const MerchantBackupTerminalDeviceRecordSchema: z.ZodType<MerchantBackupTerminalDeviceRecord> =
    z.object({
        terminal_device_uuid: z.string().uuid(),
        logical_terminal_uuid: z.string().uuid(),
        processor_device_id: z.string().min(1),
        organization_uuid: z.string().uuid().nullable(),
        applies_to_children: z.boolean().nullable(),
        name: z.string().nullable(),
        is_virtual: z.boolean(),
        status: z.enum(Status),
        terminal_device_configuration: StoredTerminalDeviceConfigurationSchema,
        credential_uuid: z.string().uuid().nullable(),
        credential_credential_type: z.enum(CredentialType).nullable(),
        omnisearch_text: z.string(),
    });

export const MerchantBackupModeSchema: z.ZodType<MerchantBackupMode> = z.enum([
    'configuration',
    'full',
]);

export const MerchantBackupPasswordRecordSchema: z.ZodType<MerchantBackupPasswordRecord> = z.object(
    {
        password_uuid: z.string().uuid(),
        user_uuid: z.string().uuid(),
        password_hash: z.string().nullable(),
        created_ts: z.string().min(1),
        status: z.enum(['ACTIVE', 'RETIRED', 'RESET_REQUIRED', 'NOT_SET', 'DELETED']),
    }
);

export const MerchantBackupTotpSecretRecordSchema: z.ZodType<MerchantBackupTotpSecretRecord> =
    z.object({
        totp_secret_uuid: z.string().uuid(),
        user_uuid: z.string().uuid(),
        totp_secret: z.string().min(1),
        created_ts: z.string().min(1),
        status: z.enum(['PENDING_SETUP', 'ACTIVE', 'RETIRED', 'DISABLED', 'DELETED']),
    });

export const MerchantBackupTotpBackupCodeRecordSchema: z.ZodType<MerchantBackupTotpBackupCodeRecord> =
    z.object({
        totp_backup_code_uuid: z.string().uuid(),
        user_uuid: z.string().uuid(),
        code_hash: z.string().min(1),
        used_ts: z.string().min(1).nullable(),
    });

export const MerchantBackupDocumentTemplateRecordSchema: z.ZodType<MerchantBackupDocumentTemplateRecord> =
    z.object({
        document_template_uuid: z.string().uuid(),
        organization_uuid: z.string().uuid(),
        template_kind: z.enum([
            'PAYMENT_RECEIPT',
            'INVOICE',
            'PAYMENT_REQUEST_EMAIL',
            'PAYMENT_REQUEST_SMS',
        ]),
        template_output_mime: z.enum(['TEXT', 'HTML']),
        payload_type: z.enum(['TRANSACTION_DETAILS', 'PAYMENT_REQUEST', 'PAYMENT_TRANSACTION']),
        payload_discriminator: z.string().min(1),
        payload_type_version: z.number().int(),
        selector_json: DocumentTemplateSelectorJsonSchema.nullable(),
        name: z.string().min(1),
        description: z.string().nullable(),
        template_source: z.string(),
        content_sha256: z.string().min(1),
        status: z.enum(Status),
        omnisearch_text: z.string(),
    });

export const MerchantBackupLegalDocumentRecordSchema: z.ZodType<MerchantBackupLegalDocumentRecord> =
    z.object({
        legal_document_uuid: z.string().uuid(),
        legal_document_title: z.string().min(1),
        legal_document_version: z.string().min(1),
        status: z.enum(Status),
        legal_document_text: z.string(),
        legal_document_content_type: z.string().min(1),
        content_sha256: z.string().min(1),
        source_filename: z.string().nullable(),
        content_length_bytes: z.number().int().nonnegative(),
        in_force_start: z.string().min(1),
        in_force_end: z.string().min(1).nullable(),
        required_for_users: z.boolean(),
        required_for_orgs: z.boolean(),
    });

export const MerchantBackupProcessorRecordSchema: z.ZodType<MerchantBackupProcessorRecord> =
    z.object({
        processor_uuid: z.string().uuid(),
        processor_name: z.string().min(1),
        status: z.enum(Status),
        processor_config: JsonRecordSchema,
        omnisearch_text: z.string(),
    });

export const MerchantBackupMerchantCustomerRecordSchema: z.ZodType<MerchantBackupMerchantCustomerRecord> =
    z.object({
        merchant_customer_record_uuid: z.string().uuid(),
        customer_organization_uuid: z.string().uuid().nullable(),
        name: z.string().min(1),
        is_organizational: z.boolean(),
        primary_email: z.string().nullable(),
        primary_phone: z.string().nullable(),
        customer_details: MerchantBackupCustomerDetailsSchema,
        merchant_organization_uuid: z.string().uuid(),
        status: z.enum(Status),
        omnisearch_text: z.string(),
    });

export const MerchantBackupIpwCustomerLinkRecordSchema: z.ZodType<MerchantBackupIpwCustomerLinkRecord> =
    z.object({
        ipw_customer_link_uuid: z.string().uuid(),
        merchant_customer_record_uuid: z.string().uuid(),
        ipw_system_id: z.number().int(),
        ipw_customer_id: z.string().min(1),
        status: z.enum(Status),
    });

export const MerchantBackupStoredPaymentMethodRecordSchema: z.ZodType<MerchantBackupStoredPaymentMethodRecord> =
    z.object({
        stored_payment_method_uuid: z.string().uuid(),
        holder_organization_uuid: z.string().uuid(),
        payment_rail: z.enum(PaymentRail),
        status: z.enum(Status),
        customer_details: MerchantBackupCustomerDetailsSchema.nullable(),
    });

export const MerchantBackupCreditCardStoredPaymentDetailsRecordSchema: z.ZodType<MerchantBackupCreditCardStoredPaymentDetailsRecord> =
    z.object({
        credit_card_stored_payment_details_uuid: z.string().uuid(),
        stored_payment_method_uuid: z.string().uuid(),
        status: z.enum(Status),
    });

export const MerchantBackupDebitCardStoredPaymentDetailsRecordSchema: z.ZodType<MerchantBackupDebitCardStoredPaymentDetailsRecord> =
    z.object({
        debit_card_stored_payment_details_uuid: z.string().uuid(),
        stored_payment_method_uuid: z.string().uuid(),
        status: z.enum(Status),
    });

export const MerchantBackupAchStoredPaymentDetailsRecordSchema: z.ZodType<MerchantBackupAchStoredPaymentDetailsRecord> =
    z.object({
        ach_stored_payment_details_uuid: z.string().uuid(),
        stored_payment_method_uuid: z.string().uuid(),
        status: z.enum(Status),
    });

export const MerchantBackupPaymentMethodTokenRecordSchema: z.ZodType<MerchantBackupPaymentMethodTokenRecord> =
    z.object({
        payment_method_token_uuid: z.string().uuid(),
        holder_organization_uuid: z.string().uuid(),
        payment_rail: z.enum(PaymentRail),
        status: z.enum(Status),
        gateway_id: z.number().int(),
        gateway_token: z.string().min(1),
        created_by_transaction_uuid: z.string().uuid().nullable(),
        stored_payment_method_uuid: z.string().uuid().nullable(),
        created_ts: z.string().min(1),
        stored_for_future_use: z.boolean(),
        payment_method_token_details: z.lazy(() => PaymentMethodTokenDetailsSchema).nullable(),
        customer_details: MerchantBackupCustomerDetailsSchema.nullable(),
    });

export const MerchantBackupPaymentRequestRecordSchema: z.ZodType<MerchantBackupPaymentRequestRecord> =
    z.object({
        payment_request_uuid: z.string().uuid(),
        merchant_organization_uuid: z.string().uuid(),
        merchant_user_uuid: z.string().uuid(),
        customer_organization_uuid: z.string().uuid().nullable(),
        customer_user_uuid: z.string().uuid().nullable(),
        merchant_customer_record_uuid: z.string().uuid().nullable(),
        reissued_from_payment_request_uuid: z.string().uuid().nullable(),
        terminal_device_uuid: z.string().uuid().nullable(),
        status: z.enum(PaymentRequestStatus),
        created_by_app: z.enum(['IPS', 'PILLAR']).nullable(),
        created_by_ux_state: z.string().nullable(),
        source_ipw_system_id: z.number().int().nullable(),
        source_document_reference: z.string().nullable(),
        source_document_key: z.lazy(() => PaymentRequestSourceDocumentKeySchema).nullable(),
        title: z.string().nullable(),
        message: z.string().nullable(),
        recipient_email: z.string().nullable(),
        recipient_phone: z.string().nullable(),
        payment_request_details: z.lazy(() => TransactionDetailsSchema).nullable(),
        original_amount: z.string().nullable(),
        amount_breakdown: z.lazy(() => PaymentTransactionAmountBreakdownSchema).nullable(),
        currency_code: z.string().min(1),
        email_opt_in: z.boolean(),
        sms_opt_in: z.boolean(),
        reconciled: z.enum(PaymentReconciliationStatus).nullable(),
        omnisearch_text: z.string(),
        created_ts: z.string().min(1),
        sent_ts: z.string().min(1).nullable(),
        viewed_ts: z.string().min(1).nullable(),
        expires_ts: z.string().min(1).nullable(),
        fulfilled_ts: z.string().min(1).nullable(),
        canceled_ts: z.string().min(1).nullable(),
    });

export const MerchantBackupPaymentRequestDeliveryEventRecordSchema: z.ZodType<MerchantBackupPaymentRequestDeliveryEventRecord> =
    z.object({
        payment_request_delivery_event_uuid: z.string().uuid(),
        payment_request_uuid: z.string().uuid(),
        delivery_channel: z.enum(DeliveryChannel),
        delivery_provider: z.enum(DeliveryProvider),
        provider_message_id: z.string().nullable(),
        provider_event_type: z.string().min(1),
        provider_event_ts: z.string().min(1).nullable(),
        received_ts: z.string().min(1),
        provider_payload: z.unknown().nullable(),
    });

export const MerchantBackupPaymentRequestDocumentRecordSchema: z.ZodType<MerchantBackupPaymentRequestDocumentRecord> =
    z
        .object({
            payment_request_document_uuid: z.string().uuid(),
            payment_request_uuid: z.string().uuid(),
            merchant_organization_uuid: z.string().uuid(),
            document_template_uuid: z.string().uuid().nullable(),
            source_kind: z.enum(['UPLOADED', 'GENERATED']),
            storage_object_key: z.string().min(1),
            original_filename: z.string().nullable(),
            media_type: z.string().min(1),
            content_length_bytes: z.string().min(1),
            content_sha256: z.string().min(1),
            content_base64: z.string().min(1).optional(),
        })
        .strict();

export const MerchantBackupPaymentTransactionRecordSchema: z.ZodType<MerchantBackupPaymentTransactionRecord> =
    z.object({
        payment_transaction_uuid: z.string().uuid(),
        merchant_organization_uuid: z.string().uuid(),
        merchant_user_uuid: z.string().uuid(),
        terminal_device_uuid: z.string().uuid(),
        payment_request_uuid: z.string().uuid().nullable(),
        transaction_configuration: z.lazy(() => PaymentTransactionConfigurationSchema),
        merchant_transaction_id: z.string().min(1),
        merchant_customer_record_uuid: z.string().uuid().nullable(),
        payment_method_token_uuid: z.string().uuid().nullable(),
        card_type: z.enum(CardType).nullable(),
        card_brand: z.string().nullable(),
        card_last4: z.string().nullable(),
        cardholder_name: z.string().nullable(),
        gateway_id: z.number().int(),
        gateway_transaction_type: z.string().min(1),
        gateway_txn_id: z.string().nullable(),
        gateway_batch: z.string().nullable(),
        gateway_stan: z.string().nullable(),
        gateway_rrn: z.string().nullable(),
        gateway_auth_code: z.string().nullable(),
        currency_code: z.string().nullable(),
        amount_breakdown: z.lazy(() => PaymentTransactionAmountBreakdownSchema).nullable(),
        original_amount: z.string().min(1),
        batched_amount: z.string().nullable(),
        refunded_amount: z.string().nullable(),
        transaction_created_ts: z.string().min(1),
        auth_decision_ts: z.string().min(1).nullable(),
        status: z.enum(PaymentTransactionStatus),
        reconciled: z.enum(PaymentReconciliationStatus),
        ipw_system_id: z.number().int(),
        ipw_customer_id: z.string().nullable(),
        ipw_transaction_details: z.lazy(() => TransactionDetailsSchema).nullable(),
        omnisearch_text: z.string(),
    });

export const MerchantBackupPaymentTransactionLifecycleEventRecordSchema: z.ZodType<MerchantBackupPaymentTransactionLifecycleEventRecord> =
    z.object({
        payment_transaction_lifecycle_event_uuid: z.string().uuid(),
        payment_transaction_uuid: z.string().uuid(),
        event_type: z.enum(PaymentTransactionLifecycleEventType),
        event_start: z.string().min(1),
        event_end: z.string().min(1),
        transaction_status: z.enum(PaymentTransactionStatus).nullable(),
        reconciled: z.enum(PaymentReconciliationStatus).nullable(),
        gateway_request: GatewayLifecycleRequestSchema.nullable(),
        gateway_response_http_status: z.number().int().nullable(),
        gateway_response_header: z.lazy(() => GatewayResponseHeaderSchema).nullable(),
        gateway_response_json: GatewayLifecycleResponseJsonBodySchema.nullable(),
        gateway_response_raw: z.string().nullable(),
        gateway_stan: z.string().nullable(),
        gateway_rrn: z.string().nullable(),
        gateway_auth_code: z.string().nullable(),
        gateway_request_type: z.string().nullable(),
        gateway_response_type: z.string().nullable(),
        gateway_endpoint_url: z.string().nullable(),
        pillar_endpoint_url: z.string().nullable(),
        pillar_request: z.lazy(() => PillarPaymentRequestSchema).nullable(),
        pillar_response: z.lazy(() => PillarPaymentResponseSchema).nullable(),
        error_code: z.string().nullable(),
        error_message: z.string().nullable(),
    });

export const MerchantBackupUserLegalDocumentAcceptanceRecordSchema: z.ZodType<MerchantBackupUserLegalDocumentAcceptanceRecord> =
    z
        .object({
            user_legal_document_acceptance_uuid: z.string().uuid(),
            legal_document_uuid: z.string().uuid(),
            user_uuid: z.string().uuid(),
            accepted_ts: z.string().min(1),
            accepted_ip: z.string().nullable(),
            accepted_user_agent: z.string().nullable(),
            accepted_role_assignment_uuid: z.string().uuid().nullable(),
        })
        .strict();

export const MerchantBackupIpxCredentialDefinitionRecordSchema = z
    .object({
        ipx_credential_definition_uuid: z.string().uuid(),
        name: z.string().min(1),
        system_id: z.number().int(),
        credential_definition_key: z.string(),
        credential_definition_kind: z.enum(IpxCredentialDefinitionKind),
        credential_type: z.enum(CredentialType),
        credential_owner_scope: CredentialOwnerScopeSchema,
        trigger_url_pattern: z.unknown().nullable(),
        credential_field_config: JsonRecordSchema,
        login_workflow: z.unknown().nullable(),
        status: z.enum(Status),
        ipx_web_resource_model_uuid: z.string().uuid().nullable(),
        omnisearch_text: z.string(),
    })
    .strict() satisfies z.ZodType<MerchantBackupIpxCredentialDefinitionRecord>;

export const MerchantBackupIpxCredentialDefinitionRecordV2Schema: z.ZodType<MerchantBackupIpxCredentialDefinitionRecordV2> =
    MerchantBackupIpxCredentialDefinitionRecordSchema.omit({
        login_workflow: true,
        ipx_web_resource_model_uuid: true,
    });

export const MerchantBackupIpxWebResourceModelRecordSchema: z.ZodType<MerchantBackupIpxWebResourceModelRecord> =
    z
        .object({
            ipx_web_resource_model_uuid: z.string().uuid(),
            system_id: z.number().int(),
            initial_navigation_url: z.string().min(1),
            web_resource_model: JsonRecordSchema,
            status: z.enum(Status),
            omnisearch_text: z.string(),
        })
        .strict();

export const MerchantBackupAllowedWebsiteTemplateRecordSchema: z.ZodType<MerchantBackupAllowedWebsiteTemplateRecord> =
    z
        .object({
            allowed_website_template_uuid: z.string().uuid(),
            system_id: z.number().int(),
            name: z.string().min(1),
            status: z.enum(Status),
            website_config: StoredUnmanagedAllowedWebsiteSchema,
            omnisearch_text: z.string(),
        })
        .strict();

export const MerchantBackupAllowedWebsiteTemplateRecordV2Schema: z.ZodType<MerchantBackupAllowedWebsiteTemplateRecordV2> =
    z
        .object({
            allowed_website_template_uuid: z.string().uuid(),
            system_id: z.number().int(),
            name: z.string().min(1),
            status: z.enum(Status),
            website_config: StoredUnmanagedAllowedWebsiteSchema,
            omnisearch_text: z.string(),
            rbs_website: StoredRbsWebsiteStorageV1Schema.nullable(),
            ipx_credential_definition_uuid: z.string().uuid().nullable(),
        })
        .strict();

export const MerchantBackupOrganizationAllowedWebsiteRecordSchema: z.ZodType<MerchantBackupOrganizationAllowedWebsiteRecord> =
    z
        .object({
            organization_allowed_website_uuid: z.string().uuid(),
            organization_uuid: z.string().uuid(),
            source: z.enum(OrganizationAllowedWebsiteSource),
            status: z.enum(Status),
            display_order: z.number().int().nullable(),
            allowed_website_template_uuid: z.string().uuid().nullable(),
            copied_from_allowed_website_template_uuid: z.string().uuid().nullable(),
            website_config: StoredUnmanagedAllowedWebsiteSchema.nullable(),
        })
        .strict();

export const MerchantBackupOrganizationAllowedWebsiteRecordV2Schema: z.ZodType<MerchantBackupOrganizationAllowedWebsiteRecordV2> =
    z
        .object({
            organization_allowed_website_uuid: z.string().uuid(),
            organization_uuid: z.string().uuid(),
            source: z.enum(OrganizationAllowedWebsiteSource),
            status: z.enum(Status),
            display_order: z.number().int().nullable(),
            allowed_website_template_uuid: z.string().uuid().nullable(),
            copied_from_allowed_website_template_uuid: z.string().uuid().nullable(),
            website_config: StoredUnmanagedAllowedWebsiteSchema.nullable(),
            rbs_website: StoredRbsWebsiteStorageV1Schema.nullable(),
            ipx_credential_definition_uuid: z.string().uuid().nullable(),
        })
        .strict();

export const MerchantBackupOrganizationRpaBuilderWebsiteRecordSchema: z.ZodType<MerchantBackupOrganizationRpaBuilderWebsiteRecord> =
    z
        .object({
            organization_rpa_builder_website_uuid: z.string().uuid(),
            organization_uuid: z.string().uuid(),
            source: z.enum(OrganizationAllowedWebsiteSource),
            status: z.enum(Status),
            display_order: z.number().int().nullable(),
            allowed_website_template_uuid: z.string().uuid().nullable(),
            copied_from_allowed_website_template_uuid: z.string().uuid().nullable(),
            website_config: StoredUnmanagedAllowedWebsiteSchema.nullable(),
            rbs_config: StoredRpaBuilderWebsiteConfigSchema,
        })
        .strict();

export const MerchantBackupIpxRbsWorkflowInstanceRecordSchema: z.ZodType<MerchantBackupIpxRbsWorkflowInstanceRecord> =
    z
        .object({
            ipx_rbs_workflow_instance_uuid: z.string().uuid(),
            workflow_class: z.string().min(1),
            workflow_order: z.number().int(),
            name: z.string().min(1),
            enabled: z.boolean(),
            status: z.enum(Status),
            workflow: z.unknown(),
            input_bindings: z.array(z.unknown()),
            output_bindings: z.array(z.unknown()),
            class_metadata: JsonRecordSchema,
            ipx_web_resource_model_revision: z.string().min(1),
            ipx_web_resource_model_uuid: z.string().uuid(),
            allowed_website_template_uuid: z.string().uuid().nullable(),
            organization_rpa_builder_website_uuid: z.string().uuid().nullable(),
        })
        .strict();

export const MerchantBackupOrganizationIpxCredentialPolicyRecordSchema: z.ZodType<MerchantBackupOrganizationIpxCredentialPolicyRecord> =
    z
        .object({
            organization_ipx_credential_policy_uuid: z.string().uuid(),
            organization_uuid: z.string().uuid(),
            ipx_credential_definition_uuid: z.string().uuid(),
            status: z.enum(Status),
        })
        .strict();

export const MerchantBackupCredentialRotationAttemptRecordSchema: z.ZodType<MerchantBackupCredentialRotationAttemptRecord> =
    z
        .object({
            credential_rotation_attempt_uuid: z.string().uuid(),
            credential_uuid: z.string().uuid(),
            organization_uuid: z.string().uuid(),
            role_assignment_uuid: z.string().uuid().nullable(),
            ipx_credential_definition_uuid: z.string().uuid(),
            ipx_web_resource_model_uuid: z.string().uuid().nullable(),
            actor_role_assignment_uuid: z.string().uuid().nullable(),
            flow_key: z.string().min(1),
            state: z.enum(CredentialRotationAttemptState),
            active_attempt_dedupe_key: z.string().nullable(),
            target_field_keys: CredentialRotationTargetFieldKeysSchema,
            credential_definition_digest: z.string().min(1),
            ipx_web_resource_model_digest: z.string().min(1).nullable(),
            credential_payload: JsonRecordSchema,
            generation_metadata: CredentialRotationGenerationMetadataSchema,
            success_evidence: CredentialRotationPersistenceEvidenceSchema.nullable(),
            failure_evidence: CredentialRotationPersistenceEvidenceSchema.nullable(),
            expires_ts: z.string().min(1),
            created_ts: z.string().min(1),
            injected_ts: z.string().min(1).nullable(),
            submitted_ts: z.string().min(1).nullable(),
            promoted_ts: z.string().min(1).nullable(),
            failed_ts: z.string().min(1).nullable(),
        })
        .strict();

export const MerchantBackupPaymentRequestAccessTokenRecordSchema: z.ZodType<MerchantBackupPaymentRequestAccessTokenRecord> =
    z
        .object({
            single_use_auth_token_uuid: z.string().uuid(),
            type: z.literal('PAYMENT_REQUEST_ACCESS'),
            token_value: z.string().min(1),
            user_uuid: z.string().uuid().nullable(),
            payment_request_uuid: z.string().uuid(),
            email: z.string().nullable(),
            phone: z.string().nullable(),
            payload: z.unknown().nullable(),
            created_ts: z.string().min(1),
            expires_ts: z.string().min(1),
            used_ts: z.string().min(1).nullable(),
        })
        .strict();

const MerchantBackupBaseSchema = z
    .object({
        backup_mode: MerchantBackupModeSchema.default('configuration'),
        exported_at_iso: z.string().min(1),
        root_organization_uuid: z.string().uuid(),
        organizations: z.array(MerchantBackupOrganizationRecordSchema),
        users: z.array(MerchantBackupUserRecordSchema),
        permissions_bundles: z.array(MerchantBackupPermissionsBundleRecordSchema),
        role_assignments: z.array(MerchantBackupRoleAssignmentRecordSchema),
        permissions_policy_grants: z.array(MerchantBackupPermissionsPolicyGrantRecordSchema),
        credentials: z.array(MerchantBackupCredentialRecordSchema),
        merchant_accounts: z.array(MerchantBackupMerchantAccountRecordSchema),
        logical_terminals: z.array(MerchantBackupLogicalTerminalRecordSchema),
        terminal_devices: z.array(MerchantBackupTerminalDeviceRecordSchema),
        passwords: z.array(MerchantBackupPasswordRecordSchema).default([]),
        totp_secrets: z.array(MerchantBackupTotpSecretRecordSchema).default([]),
        totp_backup_codes: z.array(MerchantBackupTotpBackupCodeRecordSchema).default([]),
        document_templates: z.array(MerchantBackupDocumentTemplateRecordSchema).default([]),
        legal_documents: z.array(MerchantBackupLegalDocumentRecordSchema).default([]),
        processors: z.array(MerchantBackupProcessorRecordSchema).default([]),
        payment_method_tokens: z.array(MerchantBackupPaymentMethodTokenRecordSchema).default([]),
        stored_payment_methods: z.array(MerchantBackupStoredPaymentMethodRecordSchema).default([]),
        credit_card_stored_payment_details: z
            .array(MerchantBackupCreditCardStoredPaymentDetailsRecordSchema)
            .default([]),
        debit_card_stored_payment_details: z
            .array(MerchantBackupDebitCardStoredPaymentDetailsRecordSchema)
            .default([]),
        ach_stored_payment_details: z
            .array(MerchantBackupAchStoredPaymentDetailsRecordSchema)
            .default([]),
        payment_transactions: z.array(MerchantBackupPaymentTransactionRecordSchema).default([]),
        payment_requests: z.array(MerchantBackupPaymentRequestRecordSchema).default([]),
        payment_request_delivery_events: z
            .array(MerchantBackupPaymentRequestDeliveryEventRecordSchema)
            .default([]),
        payment_request_documents: z
            .array(MerchantBackupPaymentRequestDocumentRecordSchema)
            .default([]),
        payment_transaction_lifecycle_events: z
            .array(MerchantBackupPaymentTransactionLifecycleEventRecordSchema)
            .default([]),
        ipw_customer_links: z.array(MerchantBackupIpwCustomerLinkRecordSchema).default([]),
        merchant_customer_records: z.array(MerchantBackupMerchantCustomerRecordSchema).default([]),
        user_legal_document_acceptances: z
            .array(MerchantBackupUserLegalDocumentAcceptanceRecordSchema)
            .default([]),
        ipx_credential_definitions: z
            .array(MerchantBackupIpxCredentialDefinitionRecordSchema)
            .optional(),
        ipx_web_resource_models: z.array(MerchantBackupIpxWebResourceModelRecordSchema).optional(),
        allowed_website_templates: z
            .array(MerchantBackupAllowedWebsiteTemplateRecordSchema)
            .optional(),
        ipx_rbs_workflow_instances: z
            .array(MerchantBackupIpxRbsWorkflowInstanceRecordSchema)
            .optional(),
        organization_ipx_credential_policies: z
            .array(MerchantBackupOrganizationIpxCredentialPolicyRecordSchema)
            .optional(),
        credential_rotation_attempts: z
            .array(MerchantBackupCredentialRotationAttemptRecordSchema)
            .optional(),
        payment_request_access_tokens: z
            .array(MerchantBackupPaymentRequestAccessTokenRecordSchema)
            .optional(),
    })
    .strict();

function validateMerchantBackupFullMode(backup: MerchantBackup, ctx: z.RefinementCtx): void {
    if (backup.backup_mode !== 'full') {
        return;
    }

    if (backup.ipx_credential_definitions === undefined) {
        addMerchantBackupFullModeRequiredFieldIssue(ctx, [], 'ipx_credential_definitions');
    }
    if (backup.ipx_web_resource_models === undefined) {
        addMerchantBackupFullModeRequiredFieldIssue(ctx, [], 'ipx_web_resource_models');
    }
    if (backup.allowed_website_templates === undefined) {
        addMerchantBackupFullModeRequiredFieldIssue(ctx, [], 'allowed_website_templates');
    }
    if (backup.organization_allowed_websites === undefined) {
        addMerchantBackupFullModeRequiredFieldIssue(ctx, [], 'organization_allowed_websites');
    }
    if (backup.schema_version === 1 && backup.organization_rpa_builder_websites === undefined) {
        addMerchantBackupFullModeRequiredFieldIssue(ctx, [], 'organization_rpa_builder_websites');
    }
    if (backup.ipx_rbs_workflow_instances === undefined) {
        addMerchantBackupFullModeRequiredFieldIssue(ctx, [], 'ipx_rbs_workflow_instances');
    }
    if (backup.organization_ipx_credential_policies === undefined) {
        addMerchantBackupFullModeRequiredFieldIssue(
            ctx,
            [],
            'organization_ipx_credential_policies'
        );
    }
    if (backup.credential_rotation_attempts === undefined) {
        addMerchantBackupFullModeRequiredFieldIssue(ctx, [], 'credential_rotation_attempts');
    }
    if (backup.payment_request_access_tokens === undefined) {
        addMerchantBackupFullModeRequiredFieldIssue(ctx, [], 'payment_request_access_tokens');
    }

    backup.organizations.forEach((record: MerchantBackupOrganizationRecord, index: number) => {
        if (record.iso_organization_uuid === undefined) {
            addMerchantBackupFullModeRequiredFieldIssue(
                ctx,
                ['organizations', index],
                'iso_organization_uuid'
            );
        }
        if (record.enable_ipg_system_ids === undefined) {
            addMerchantBackupFullModeRequiredFieldIssue(
                ctx,
                ['organizations', index],
                'enable_ipg_system_ids'
            );
        }
        if (record.enable_ipw_system_ids === undefined) {
            addMerchantBackupFullModeRequiredFieldIssue(
                ctx,
                ['organizations', index],
                'enable_ipw_system_ids'
            );
        }
    });

    backup.users.forEach((record: MerchantBackupUserRecord, index: number) => {
        if (record.phones === undefined) {
            addMerchantBackupFullModeRequiredFieldIssue(ctx, ['users', index], 'phones');
        }
        if (record.primary_phone === undefined) {
            addMerchantBackupFullModeRequiredFieldIssue(ctx, ['users', index], 'primary_phone');
        }
        if (record.mfa_required === undefined) {
            addMerchantBackupFullModeRequiredFieldIssue(ctx, ['users', index], 'mfa_required');
        }
        if (record.mfa_email_allowed === undefined) {
            addMerchantBackupFullModeRequiredFieldIssue(ctx, ['users', index], 'mfa_email_allowed');
        }
        if (record.mfa_sms_allowed === undefined) {
            addMerchantBackupFullModeRequiredFieldIssue(ctx, ['users', index], 'mfa_sms_allowed');
        }
        if (record.custom_config === undefined) {
            addMerchantBackupFullModeRequiredFieldIssue(ctx, ['users', index], 'custom_config');
        }
    });

    backup.permissions_bundles.forEach(
        (record: MerchantBackupPermissionsBundleRecord, index: number) => {
            if (record.status === undefined) {
                addMerchantBackupFullModeRequiredFieldIssue(
                    ctx,
                    ['permissions_bundles', index],
                    'status'
                );
            }
        }
    );

    backup.role_assignments.forEach((record: MerchantBackupRoleAssignmentRecord, index: number) => {
        if (record.status === undefined) {
            addMerchantBackupFullModeRequiredFieldIssue(ctx, ['role_assignments', index], 'status');
        }
    });

    backup.permissions_policy_grants.forEach(
        (record: MerchantBackupPermissionsPolicyGrantRecord, index: number) => {
            if (record.status === undefined) {
                addMerchantBackupFullModeRequiredFieldIssue(
                    ctx,
                    ['permissions_policy_grants', index],
                    'status'
                );
            }
        }
    );

    backup.credentials.forEach((record: MerchantBackupCredentialRecord, index: number) => {
        if (record.role_assignment_uuid === undefined) {
            addMerchantBackupFullModeRequiredFieldIssue(
                ctx,
                ['credentials', index],
                'role_assignment_uuid'
            );
        }
        if (record.ipx_credential_definition_uuid === undefined) {
            addMerchantBackupFullModeRequiredFieldIssue(
                ctx,
                ['credentials', index],
                'ipx_credential_definition_uuid'
            );
        }
        if (record.credential_definition_key === undefined) {
            addMerchantBackupFullModeRequiredFieldIssue(
                ctx,
                ['credentials', index],
                'credential_definition_key'
            );
        }
        if (record.credential_payload === undefined) {
            addMerchantBackupFullModeRequiredFieldIssue(
                ctx,
                ['credentials', index],
                'credential_payload'
            );
        }
        if (record.encrypted_credentials_base64 !== undefined) {
            addMerchantBackupFullModeRejectedFieldIssue(
                ctx,
                ['credentials', index],
                'encrypted_credentials_base64'
            );
        }
        if (record.encryption_algorithm !== undefined) {
            addMerchantBackupFullModeRejectedFieldIssue(
                ctx,
                ['credentials', index],
                'encryption_algorithm'
            );
        }
    });

    backup.payment_request_documents.forEach(
        (record: MerchantBackupPaymentRequestDocumentRecord, index: number) => {
            if (record.content_base64 === undefined) {
                addMerchantBackupFullModeRequiredFieldIssue(
                    ctx,
                    ['payment_request_documents', index],
                    'content_base64'
                );
            }
        }
    );
}

export const MerchantBackupV1Schema: z.ZodType<MerchantBackupV1> = MerchantBackupBaseSchema.extend({
    schema_version: z.literal(1),
    organization_allowed_websites: z
        .array(MerchantBackupOrganizationAllowedWebsiteRecordSchema)
        .optional(),
    organization_rpa_builder_websites: z
        .array(MerchantBackupOrganizationRpaBuilderWebsiteRecordSchema)
        .optional(),
})
    .strict()
    .superRefine(validateMerchantBackupFullMode);

export const MerchantBackupV2Schema: z.ZodType<MerchantBackupV2> = MerchantBackupBaseSchema.extend({
    schema_version: z.literal(2),
    ipx_credential_definitions: z
        .array(MerchantBackupIpxCredentialDefinitionRecordV2Schema)
        .optional(),
    allowed_website_templates: z
        .array(MerchantBackupAllowedWebsiteTemplateRecordV2Schema)
        .optional(),
    organization_allowed_websites: z
        .array(MerchantBackupOrganizationAllowedWebsiteRecordV2Schema)
        .optional(),
})
    .strict()
    .superRefine(validateMerchantBackupFullMode);

export const MerchantBackupSchema: z.ZodType<MerchantBackup> = z.union([
    MerchantBackupV1Schema,
    MerchantBackupV2Schema,
]);

export const ExportMerchantBackupRequestSchema: z.ZodType<ExportMerchantBackupRequest> = z.object({
    mode: MerchantBackupModeSchema.optional(),
});

export const ExportMerchantBackupResponseSchema: z.ZodType<
    import('../organization.types').ExportMerchantBackupResponse
> = z.object({
    backup: MerchantBackupSchema,
});

export const MerchantBackupRestoreSummarySchema: z.ZodType<MerchantBackupRestoreSummary> = z.object(
    {
        organizations: z.number().int().nonnegative(),
        users: z.number().int().nonnegative(),
        permissions_bundles: z.number().int().nonnegative(),
        role_assignments: z.number().int().nonnegative(),
        permissions_policy_grants: z.number().int().nonnegative(),
        credentials: z.number().int().nonnegative(),
        merchant_accounts: z.number().int().nonnegative(),
        logical_terminals: z.number().int().nonnegative(),
        terminal_devices: z.number().int().nonnegative(),
        passwords: z.number().int().nonnegative().default(0),
        totp_secrets: z.number().int().nonnegative().default(0),
        totp_backup_codes: z.number().int().nonnegative().default(0),
        document_templates: z.number().int().nonnegative().default(0),
        legal_documents: z.number().int().nonnegative().default(0),
        processors: z.number().int().nonnegative().default(0),
        payment_method_tokens: z.number().int().nonnegative().default(0),
        stored_payment_methods: z.number().int().nonnegative().default(0),
        credit_card_stored_payment_details: z.number().int().nonnegative().default(0),
        debit_card_stored_payment_details: z.number().int().nonnegative().default(0),
        ach_stored_payment_details: z.number().int().nonnegative().default(0),
        payment_transactions: z.number().int().nonnegative().default(0),
        payment_requests: z.number().int().nonnegative().default(0),
        payment_request_delivery_events: z.number().int().nonnegative().default(0),
        payment_request_documents: z.number().int().nonnegative().default(0),
        payment_transaction_lifecycle_events: z.number().int().nonnegative().default(0),
        ipw_customer_links: z.number().int().nonnegative().default(0),
        merchant_customer_records: z.number().int().nonnegative().default(0),
        user_legal_document_acceptances: z.number().int().nonnegative().default(0),
        ipx_credential_definitions: z.number().int().nonnegative().default(0),
        ipx_web_resource_models: z.number().int().nonnegative().default(0),
        allowed_website_templates: z.number().int().nonnegative().default(0),
        organization_allowed_websites: z.number().int().nonnegative().default(0),
        organization_rpa_builder_websites: z.number().int().nonnegative().default(0),
        ipx_rbs_workflow_instances: z.number().int().nonnegative().default(0),
        organization_ipx_credential_policies: z.number().int().nonnegative().default(0),
        credential_rotation_attempts: z.number().int().nonnegative().default(0),
        payment_request_access_tokens: z.number().int().nonnegative().default(0),
    }
);

export const RestoreMerchantBackupDryRunRequestSchema: z.ZodType<RestoreMerchantBackupDryRunRequest> =
    z.object({
        backup: MerchantBackupSchema,
    });

export const RestoreMerchantBackupDryRunResponseSchema: z.ZodType<RestoreMerchantBackupDryRunResponse> =
    z.object({
        valid: z.boolean(),
        warnings: z.array(z.string()),
        summary: MerchantBackupRestoreSummarySchema,
        collection_summaries: MerchantBackupRestoreCollectionSummaryMapSchema.optional(),
    });

export const RestoreMerchantBackupApplyRequestSchema: z.ZodType<RestoreMerchantBackupApplyRequest> =
    z.object({
        backup: MerchantBackupSchema,
    });

export const RestoreMerchantBackupApplyResponseSchema: z.ZodType<RestoreMerchantBackupApplyResponse> =
    z.object({
        target_organization_uuid: z.string().uuid(),
        target_organization_name: z.string().min(1),
        summary: MerchantBackupRestoreSummarySchema,
        collection_summaries: MerchantBackupRestoreCollectionSummaryMapSchema.optional(),
    });

export const MerchantChecklistSeveritySchema: z.ZodType<
    import('../organization.types').MerchantChecklistSeverity
> = z.enum(['error', 'warning']);
export const MerchantChecklistStatusSchema: z.ZodType<
    import('../organization.types').MerchantChecklistStatus
> = z.enum(['pass', 'error', 'warning']);
export const MerchantChecklistCheckCodeSchema: z.ZodType<
    import('../organization.types').MerchantChecklistCheckCode
> = z.enum([
    'payment_methods_enabled',
    'permission_records_valid',
    'merchant_profile_has_user',
    'users_physical_terminal_access',
    'users_virtual_terminal_access',
    'users_e_invoice_terminal_access',
    'users_payment_transaction_display_access',
    'merchant_business_details_complete',
    'payment_flow_prerequisites_complete',
    'logical_terminals_configured',
    'terminal_devices_configured',
    'terminal_device_credential_link_integrity',
    'credentials_gateway_connectivity',
    'logical_terminals_gateway_readiness',
    'terminal_devices_gateway_readiness',
    'organization_credentials_decryptable',
    'role_assignment_credentials_decryptable',
    'credentials_inconsistent_by_account_id',
    'credentials_duplicated_by_account_id',
]);

export const MerchantChecklistIssueCapabilitiesSchema: z.ZodType<MerchantChecklistIssueCapabilities> =
    z
        .object({
            can_display: z.boolean(),
        })
        .strict();

export const MerchantChecklistTerminalIssueCapabilitiesSchema: z.ZodType<MerchantChecklistTerminalIssueCapabilities> =
    z
        .object({
            can_display: z.boolean(),
            can_display_credential: z.boolean(),
        })
        .strict();

export const MerchantChecklistUserSummarySchema: z.ZodType<MerchantChecklistUserIssue['user']> = z
    .object({
        user_uuid: z.string().uuid(),
        primary_email: z.string().min(1),
        first_name: z.string().nullable(),
        last_name: z.string().nullable(),
        display_name: z.string().min(1),
        capabilities: MerchantChecklistIssueCapabilitiesSchema.optional(),
    })
    .strict();

export const MerchantChecklistCredentialSummarySchema: z.ZodType<
    MerchantChecklistCredentialIssue['credential']
> = z.object({
    credential_uuid: z.string().uuid(),
    account_id: z.string().min(1),
    system_id: z.number().int(),
    credential_type: z.enum(CredentialType),
    status: z.enum(Status),
    applies_to_children: z.boolean(),
    public_fields: JsonRecordSchema,
    capabilities: IpxCredentialSummaryCapabilitiesSchema.optional(),
});

const MerchantChecklistCredentialIssueShape = {
    issue_type: z.literal('credential'),
    message: z.string().min(1),
    credential: MerchantChecklistCredentialSummarySchema,
};
export const MerchantChecklistCredentialIssueSchema: z.ZodObject<
    typeof MerchantChecklistCredentialIssueShape
> = z.object(MerchantChecklistCredentialIssueShape);

const MerchantChecklistUserIssueShape = {
    issue_type: z.literal('user'),
    message: z.string().min(1),
    user: MerchantChecklistUserSummarySchema,
};
export const MerchantChecklistUserIssueSchema: z.ZodObject<typeof MerchantChecklistUserIssueShape> =
    z.object(MerchantChecklistUserIssueShape);

export const MerchantChecklistTerminalAccessKindSchema: z.ZodType<
    MerchantChecklistTerminalAccessIssue['access_kind']
> = z.enum(['physical', 'virtual', 'e_invoice']);

export const MerchantChecklistTerminalAccessFailureReasonSchema: z.ZodType<
    MerchantChecklistTerminalAccessIssue['failure_reason']
> = z.enum([
    'no_terminal_devices_configured',
    'no_linked_credentials_configured',
    'missing_credential_permission',
    'missing_terminal_permission',
    'missing_payment_request_create_permission',
    'missing_credential_and_terminal_permissions',
]);

const MerchantChecklistTerminalAccessIssueShape = {
    issue_type: z.literal('terminal_access'),
    message: z.string().min(1),
    access_kind: MerchantChecklistTerminalAccessKindSchema,
    failure_reason: MerchantChecklistTerminalAccessFailureReasonSchema,
    user: MerchantChecklistUserSummarySchema,
};
export const MerchantChecklistTerminalAccessIssueSchema: z.ZodObject<
    typeof MerchantChecklistTerminalAccessIssueShape
> = z.object(MerchantChecklistTerminalAccessIssueShape);

const MerchantChecklistPermissionIssueShape = {
    issue_type: z.literal('permission'),
    message: z.string().min(1),
    role_assignment_uuid: z.string().uuid(),
    permissions_bundle_uuid: z.string().uuid().nullable(),
    permissions_bundle_name: z.string().min(1).nullable(),
    permission_index: z.number().int().nonnegative().nullable(),
    resource_index: z.number().int().nonnegative().nullable(),
    finding_code: PermissionGrantAuditFindingCodeSchema,
    capabilities: MerchantChecklistIssueCapabilitiesSchema.optional(),
    user: MerchantChecklistUserSummarySchema,
};
export const MerchantChecklistPermissionIssueSchema: z.ZodObject<
    typeof MerchantChecklistPermissionIssueShape
> = z.object(MerchantChecklistPermissionIssueShape);

export const MerchantChecklistTerminalSummarySchema: z.ZodType<
    MerchantChecklistTerminalIssue['terminal']
> = z
    .object({
        logical_terminal_uuid: z.string().uuid(),
        terminal_device_uuid: z.string().uuid().nullable(),
        processor_tid: z.string().min(1),
        processor_device_id: z.string().min(1).nullable(),
        is_virtual: z.boolean().nullable(),
        credential_uuid: z.string().uuid().nullable(),
        terminal_device_credential_type: z.enum(CredentialType).nullable(),
        linked_credential_type: z.enum(CredentialType).nullable(),
        capabilities: MerchantChecklistTerminalIssueCapabilitiesSchema.optional(),
    })
    .strict();

const MerchantChecklistTerminalIssueShape = {
    issue_type: z.literal('terminal'),
    message: z.string().min(1),
    terminal: MerchantChecklistTerminalSummarySchema,
};
export const MerchantChecklistTerminalIssueSchema: z.ZodObject<
    typeof MerchantChecklistTerminalIssueShape
> = z.object(MerchantChecklistTerminalIssueShape);

export const MerchantChecklistIssueSchema: z.ZodType<MerchantChecklistIssue> = z.discriminatedUnion(
    'issue_type',
    [
        MerchantChecklistCredentialIssueSchema,
        MerchantChecklistUserIssueSchema,
        MerchantChecklistTerminalAccessIssueSchema,
        MerchantChecklistPermissionIssueSchema,
        MerchantChecklistTerminalIssueSchema,
    ]
);

export const MerchantChecklistCheckResultSchema = z.object({
    code: z.string().min(1),
    title: z.string().min(1),
    severity: MerchantChecklistSeveritySchema,
    status: MerchantChecklistStatusSchema,
    details: z.array(z.string()),
    issues: z.array(MerchantChecklistIssueSchema),
});

export const MerchantChecklistSummarySchema = z.object({
    status: MerchantChecklistStatusSchema,
    error_count: z.number().int().nonnegative(),
    warning_count: z.number().int().nonnegative(),
});

export const GetMerchantChecklistResponseSchema: z.ZodType<GetMerchantChecklistResponse> = z.object(
    {
        organization_uuid: z.string().uuid(),
        generated_at_iso: z.string().min(1),
        summary: MerchantChecklistSummarySchema,
        checks: z.array(MerchantChecklistCheckResultSchema),
    }
);

export const GetMerchantChecklistCheckResponseSchema: z.ZodType<GetMerchantChecklistCheckResponse> =
    z.object({
        organization_uuid: z.string().uuid(),
        generated_at_iso: z.string().min(1),
        check: MerchantChecklistCheckResultSchema,
    });
